import { ListingLegalAgreement } from "@/components/legal/LegalPolicyLinks";
import {
  getReturnButtonLabel,
  getSafeReturnTo,
} from "@/lib/navigation/publicReturnTo";
import { router, useLocalSearchParams } from "expo-router";
import {
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  Mail,
  MapPin,
  PhoneCall,
  QrCode,
  Search,
  Share2,
  Store,
  Tags,
  type LucideIcon,
} from "lucide-react-native";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

type ValueCard = {
  title: string;
  body: string;
  Icon: LucideIcon;
};

type ProfileItem = {
  label: string;
  Icon: LucideIcon;
};

const VALUE_CARDS: ValueCard[] = [
  {
    title: "Be found online",
    body: "Help customers discover your business through Soko Grid search.",
    Icon: Search,
  },
  {
    title: "Showcase products and services",
    body: "Display what you sell or offer in a clean public profile.",
    Icon: Store,
  },
  {
    title: "Make contact easy",
    body: "Customers can call, open maps, visit your website or social links, or contact you on WhatsApp.",
    Icon: PhoneCall,
  },
  {
    title: "Share your business link",
    body: "Use one public Soko Grid link for WhatsApp, posters, social media, and customer referrals.",
    Icon: Share2,
  },
];

const PROFILE_ITEMS: ProfileItem[] = [
  { label: "Business name and logo/photo", Icon: Store },
  { label: "Products or services", Icon: BadgeCheck },
  { label: "Category and subcategory", Icon: Tags },
  { label: "Location and map link", Icon: MapPin },
  { label: "Phone and WhatsApp contact", Icon: PhoneCall },
  { label: "Website and social links", Icon: Share2 },
  { label: "QR/public profile link", Icon: QrCode },
];

const HOW_IT_WORKS = [
  "Sign in with Gmail",
  "Add your business details",
  "Pay KSh 5,000 with M-Pesa during onboarding",
  "Submit your listing for review",
  "Go live after approval",
];

function useResponsiveWidth(fallbackWidth: number) {
  const readWebWidth = useCallback(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") {
      return fallbackWidth;
    }

    return window.innerWidth;
  }, [fallbackWidth]);

  const [webWidth, setWebWidth] = useState(readWebWidth);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;

    const updateWidth = () => {
      setWebWidth(readWebWidth());
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);

    return () => {
      window.removeEventListener("resize", updateWidth);
    };
  }, [readWebWidth]);

  return Platform.OS === "web" ? webWidth : fallbackWidth;
}

function goToReturnPath(returnTo: string) {
  if (returnTo) {
    router.push(returnTo as any);
    return;
  }

  router.push("/" as any);
}

export default function ListYourBusinessPage() {
  const { width } = useWindowDimensions();
  const responsiveWidth = useResponsiveWidth(width);
  const params = useLocalSearchParams<{ returnTo?: string | string[] }>();
  const returnTo = useMemo(
    () => getSafeReturnTo(params.returnTo),
    [params.returnTo]
  );
  const isNarrow = responsiveWidth < 420;
  const shouldStackSections = responsiveWidth < 720;

  const startListing = useCallback(() => {
    router.push({
      pathname: "/(owner)/onboarding/signin",
      params: { returnTo: "/(owner)/start" },
    } as any);
  }, []);

  const contactSokoGrid = useCallback(() => {
    router.push({
      pathname: "/contact",
      params: { returnTo: "/list-your-business" },
    } as any);
  }, []);

  const handleBack = useCallback(() => {
    goToReturnPath(returnTo);
  }, [returnTo]);

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView
        style={styles.screen}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator
      >
        <View style={styles.container}>
          <View style={styles.hero}>
            <View style={styles.heroCopy}>
              <Text style={[styles.title, isNarrow ? styles.titleNarrow : null]}>
                List your business on Soko Grid
              </Text>
              <Text style={styles.subtitle}>
                Soko Grid is a business directory and online storefront for
                product and service providers in Kenya. The platform is built to
                support physical shops, freelancers, and online businesses.
                Customers can view your business, products, services, location,
                contact details, website link, social media links, QR code, and
                WhatsApp details from one simple public profile.
              </Text>

              <View style={styles.actions}>
                <Pressable
                  accessibilityRole="button"
                  onPress={startListing}
                  style={({ pressed }) => [
                    styles.primaryButton,
                    isNarrow ? styles.narrowButton : null,
                    pressed ? styles.pressed : null,
                  ]}
                >
                  <Text style={styles.primaryButtonText}>Start Listing</Text>
                  <ArrowRight size={18} color="#ffffff" strokeWidth={2.5} />
                </Pressable>

                <Pressable
                  accessibilityRole="button"
                  onPress={contactSokoGrid}
                  style={({ pressed }) => [
                    styles.secondaryButton,
                    isNarrow ? styles.narrowButton : null,
                    pressed ? styles.pressed : null,
                  ]}
                >
                  <Mail size={18} color="#0f172a" strokeWidth={2.4} />
                  <Text style={styles.secondaryButtonText}>Contact Us</Text>
                </Pressable>
              </View>
            </View>

            <View
              style={[
                styles.heroSummary,
                isNarrow ? styles.narrowHeroSummary : null,
              ]}
            >
              <View style={styles.summaryIconWrap}>
                <Store size={26} color="#0f766e" strokeWidth={2.5} />
              </View>
              <Text style={styles.summaryTitle}>Built for Kenyan businesses</Text>
              <Text style={styles.summaryText}>
                Product sellers and service providers can create one public
                profile that is easy for customers to find, trust, contact, and
                share.
              </Text>
              <View style={styles.summaryRows}>
                <View style={styles.summaryRow}>
                  <CheckCircle2 size={17} color="#0f766e" strokeWidth={2.5} />
                  <Text style={styles.summaryRowText}>Searchable profile</Text>
                </View>
                <View style={styles.summaryRow}>
                  <CheckCircle2 size={17} color="#0f766e" strokeWidth={2.5} />
                  <Text style={styles.summaryRowText}>Products and services</Text>
                </View>
                <View style={styles.summaryRow}>
                  <CheckCircle2 size={17} color="#0f766e" strokeWidth={2.5} />
                  <Text style={styles.summaryRowText}>Contact and share tools</Text>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Why list on Soko Grid?</Text>
            <View style={styles.cardGrid}>
              {VALUE_CARDS.map((card) => {
                const Icon = card.Icon;

                return (
                  <View
                    key={card.title}
                    style={[
                      styles.valueCard,
                      shouldStackSections ? styles.valueCardMobile : null,
                    ]}
                  >
                    <View
                      style={[
                        styles.cardIconWrap,
                        shouldStackSections ? styles.cardIconWrapMobile : null,
                      ]}
                    >
                      <Icon size={22} color="#0f766e" strokeWidth={2.4} />
                    </View>
                    <View style={styles.valueCardTextWrap}>
                      <Text style={styles.cardTitle}>{card.title}</Text>
                      <Text style={styles.cardText}>{card.body}</Text>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>

          <View style={styles.splitSection}>
            <View
              style={[
                styles.profilePanel,
                shouldStackSections ? styles.stackedPanel : null,
              ]}
            >
              <Text style={styles.sectionTitle}>Your profile can include</Text>
              <View style={styles.profileList}>
                {PROFILE_ITEMS.map((item) => {
                  const Icon = item.Icon;

                  return (
                    <View key={item.label} style={styles.profileItem}>
                      <View style={styles.profileIconWrap}>
                        <Icon size={18} color="#0f766e" strokeWidth={2.4} />
                      </View>
                      <Text style={styles.profileItemText}>{item.label}</Text>
                    </View>
                  );
                })}
              </View>
            </View>

            <View
              style={[
                styles.stepsPanel,
                shouldStackSections ? styles.stackedPanel : null,
              ]}
            >
              <Text style={styles.sectionTitle}>How it works</Text>
              <View style={styles.stepsList}>
                {HOW_IT_WORKS.map((step, index) => (
                  <View key={step} style={styles.stepRow}>
                    <View style={styles.stepNumber}>
                      <Text style={styles.stepNumberText}>{index + 1}</Text>
                    </View>
                    <Text style={styles.stepText}>{step}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>

          <View style={styles.subscriptionNote}>
            <BadgeCheck size={22} color="#854d0e" strokeWidth={2.4} />
            <Text style={styles.subscriptionText}>
              Basic Yearly Subscription — KSh 5,000 per year per business. Pay
              with M-Pesa. Your subscription begins once your business is
              approved and published.
            </Text>
          </View>

          <View style={styles.finalCta}>
            <View style={styles.finalCtaCopy}>
              <Text style={styles.finalCtaTitle}>
                Ready to create your Soko Grid business profile?
              </Text>
              <Text style={styles.finalCtaText}>
                Start with Gmail, add your details, and submit your listing for
                review.
              </Text>
              <ListingLegalAgreement
                linkStyle={styles.finalCtaLegalLink}
                returnTo="/list-your-business"
                style={styles.finalCtaLegalText}
              />
            </View>

            <Pressable
              accessibilityRole="button"
              onPress={startListing}
              style={({ pressed }) => [
                styles.primaryButton,
                styles.finalCtaButton,
                isNarrow ? styles.narrowButton : null,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text style={styles.primaryButtonText}>Start Listing</Text>
              <ArrowRight size={18} color="#ffffff" strokeWidth={2.5} />
            </Pressable>
          </View>

          {!!returnTo && (
            <Pressable
              accessibilityRole="button"
              onPress={handleBack}
              style={({ pressed }) => [
                styles.backButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text style={styles.backButtonText}>
                {getReturnButtonLabel(returnTo)}
              </Text>
            </Pressable>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#f8fafc",
  },
  screen: {
    flex: 1,
    backgroundColor: "#f8fafc",
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 96,
  },
  container: {
    width: "100%",
    maxWidth: 1120,
    alignSelf: "center",
    minWidth: 0,
  },
  hero: {
    paddingVertical: 8,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 22,
    alignItems: "center",
  },
  heroCopy: {
    flex: 1,
    minWidth: 260,
  },
  title: {
    maxWidth: 640,
    fontSize: 34,
    lineHeight: 40,
    fontWeight: "900",
    color: "#0f172a",
  },
  titleNarrow: {
    fontSize: 30,
    lineHeight: 36,
  },
  subtitle: {
    marginTop: 12,
    maxWidth: 690,
    fontSize: 16,
    lineHeight: 25,
    fontWeight: "700",
    color: "#475569",
  },
  actions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 22,
  },
  primaryButton: {
    minHeight: 48,
    minWidth: 154,
    borderRadius: 8,
    backgroundColor: "#0f172a",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 8,
    paddingHorizontal: 18,
  },
  primaryButtonText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#ffffff",
  },
  secondaryButton: {
    minHeight: 48,
    minWidth: 174,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 8,
    paddingHorizontal: 18,
  },
  secondaryButtonText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#0f172a",
  },
  heroSummary: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 270,
    maxWidth: 330,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#bbf7d0",
    backgroundColor: "#ffffff",
    padding: 18,
  },
  summaryIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: "#d1fae5",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 14,
  },
  summaryTitle: {
    fontSize: 18,
    lineHeight: 24,
    fontWeight: "900",
    color: "#0f172a",
  },
  summaryText: {
    marginTop: 8,
    fontSize: 14,
    lineHeight: 21,
    fontWeight: "700",
    color: "#475569",
  },
  summaryRows: {
    marginTop: 14,
    gap: 9,
  },
  summaryRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  summaryRowText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "900",
    color: "#334155",
  },
  section: {
    marginTop: 26,
  },
  sectionTitle: {
    fontSize: 22,
    lineHeight: 28,
    fontWeight: "900",
    color: "#0f172a",
  },
  cardGrid: {
    marginTop: 14,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  valueCard: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 230,
    minWidth: 230,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    padding: 16,
  },
  cardIconWrap: {
    width: 42,
    height: 42,
    borderRadius: 8,
    backgroundColor: "#ecfdf5",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: "900",
    color: "#0f172a",
  },
  cardText: {
    marginTop: 7,
    fontSize: 14,
    lineHeight: 21,
    fontWeight: "700",
    color: "#475569",
  },
  splitSection: {
    marginTop: 26,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 14,
  },
  profilePanel: {
    flex: 1,
    flexShrink: 1,
    minWidth: 280,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    padding: 18,
  },
  profileList: {
    marginTop: 14,
    gap: 10,
  },
  profileItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  profileIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 8,
    backgroundColor: "#ecfdf5",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  profileItemText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "800",
    color: "#334155",
  },
  stepsPanel: {
    flex: 1,
    flexShrink: 1,
    minWidth: 280,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    padding: 18,
  },
  stepsList: {
    marginTop: 14,
    gap: 12,
  },
  stepRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  stepNumber: {
    width: 34,
    height: 34,
    borderRadius: 8,
    backgroundColor: "#0f766e",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  stepNumberText: {
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "900",
    color: "#ffffff",
  },
  stepText: {
    flex: 1,
    fontSize: 15,
    lineHeight: 21,
    fontWeight: "900",
    color: "#0f172a",
  },
  subscriptionNote: {
    marginTop: 22,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#fde68a",
    backgroundColor: "#fffbeb",
    padding: 16,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
  },
  subscriptionText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 21,
    fontWeight: "800",
    color: "#713f12",
  },
  finalCta: {
    marginTop: 24,
    marginBottom: 4,
    borderRadius: 8,
    backgroundColor: "#0f172a",
    padding: 20,
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  finalCtaCopy: {
    flex: 1,
    minWidth: 250,
  },
  finalCtaTitle: {
    fontSize: 22,
    lineHeight: 28,
    fontWeight: "900",
    color: "#ffffff",
  },
  finalCtaText: {
    marginTop: 6,
    fontSize: 14,
    lineHeight: 21,
    fontWeight: "700",
    color: "#cbd5e1",
  },
  finalCtaLegalText: {
    marginTop: 10,
    color: "#cbd5e1",
  },
  finalCtaLegalLink: {
    color: "#bbf7d0",
  },
  finalCtaButton: {
    backgroundColor: "#16a34a",
    flexGrow: 1,
    maxWidth: 220,
  },
  valueCardMobile: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 14,
    padding: 14,
  },
  cardIconWrapMobile: {
    width: 46,
    height: 46,
    marginBottom: 0,
    flexShrink: 0,
  },
  valueCardTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  stackedPanel: {
    flexBasis: "100%",
    minWidth: 0,
    width: "100%",
  },
  narrowButton: {
    width: "100%",
    maxWidth: "100%",
  },
  narrowHeroSummary: {
    maxWidth: "100%",
  },
  backButton: {
    alignSelf: "flex-start",
    minHeight: 44,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
    marginTop: 16,
  },
  backButtonText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#0f172a",
  },
  pressed: {
    opacity: 0.82,
  },
});
